import axiosClient from './../../api/axiosClient';

/**
 * Notification Feed v2.
 *
 * Both endpoints live on the backend's shared /api router, but
 * REACT_APP_BASE_URL ends in /api/web and axios only concatenates -- so the base
 * is overridden to the server root, exactly as chats.service.js and
 * chatMessages.service.js do for the other /api routes. Dropping either the
 * prefix or the override 404s.
 **/
export const notificationsService = {
  async fetchUnreadCount() {
    return await axiosClient.get('/api/notification/unreadCount', {
      baseURL: process.env.REACT_APP_SOCKET_API_KEY,
    });
  },

  async fetchNotifications({ page, limit }) {
    return await axiosClient.post(
      '/api/notification/list',
      { page, limit },
      { baseURL: process.env.REACT_APP_SOCKET_API_KEY }
    );
  },

  async markNotificationAsRead(notificationId) {
    return await axiosClient.patch('/notification/markNotificationAsRead', { notificationId });
  },

  async markAllNotificationsAsRead() {
    return await axiosClient.patch('/notification/markAllNotificationsAsRead');
  },
};

export const isSuccessfulMutation = response => response?.data?.error === false;

/**
 * A backend failure arrives as a 200 with `error: true` as often as it does as
 * a rejection, so both have to raise the same error state. Without this the
 * error envelope parses into an empty page and the tray says the reader is all
 * caught up when in fact nothing could be read.
 */
export const isFailedListResponse = response => !response?.data || response.data.error === true;

export const parseUnreadCount = response => {
  const body = response?.data;

  if (!body || body.error === true) {
    return null;
  }

  const count = body?.data?.unreadCount;

  return Number.isInteger(count) && count >= 0 ? count : null;
};

/**
 * Notification list envelope -> a shape the model can store without further
 * checks. Anything unexpected degrades to an empty page rather than throwing,
 * so a malformed payload cannot take the dropdown down with it. `docs` is
 * coerced to an array for the same reason.
 */
export const parseNotificationList = response => {
  const empty = { docs: [], page: 1, pages: 0, total: 0 };
  const body = response?.data;

  if (!body || body.error === true) {
    return empty;
  }

  const payload = body?.data;

  if (!payload) {
    return empty;
  }

  const asCount = (value, fallback) => (Number.isInteger(value) && value >= 0 ? value : fallback);

  return {
    docs: Array.isArray(payload.docs) ? payload.docs : [],
    page: asCount(payload.page, 1),
    pages: asCount(payload.pages, 0),
    total: asCount(payload.total, 0),
  };
};
